import { AppLanguage } from '../state/store';

export const translations: Record<AppLanguage, any> = {
  en: {
    nav: { home: 'Home', program: 'Program', training: 'Training', theory: 'Theory', settings: 'Settings', setup: 'Setup', tuner: 'Tuner', about: 'About' },
    landing: {
      heroTitle: 'Master the Bass Fretboard in 30 Days',
      heroSubtitle: 'Professional real-time pitch detection for the modern bassist. Plug in your bass, play the notes, and get instant feedback.',
      ctaPrimary: 'Start Training',
      ctaSecondary: 'How it works',
      feature1Title: 'Real-time Tracking',
      feature1Desc: 'Our high-performance engine tracks your frequency with millisecond precision.',
      feature2Title: '30-Day Curriculum',
      feature2Desc: 'A granular, step-by-step program taking you from open strings to advanced positions.',
      feature3Title: 'Theory Integrated',
      feature3Desc: 'Learn the "Why" behind the "What" with built-in theory modules and scale explorers.',
      feature4Title: 'Memory Optimization',
      feature4Desc: 'Retain what you learn forever with our Spaced Repetition System (SRS) that optimizes review intervals.',
      step1Title: 'Connect',
      step1Desc: 'Plug your bass into an audio interface or use your microphone.',
      step2Title: 'Play',
      step2Desc: 'Follow the daily drills. The app listens and validates every note.',
      step3Title: 'Master',
      step3Desc: 'Track your accuracy and speed as you build permanent muscle memory.',
      srsSectionTitle: 'Engineered for Retention',
      srsSectionSubtitle: 'The Science of Spaced Repetition',
      srsSectionDesc: 'Fretboard mastery is a battle against forgetting. BassArena uses cognitive science to schedule reviews at the exact moment your memory begins to fade, ensuring every note you learn is moved from short-term memory to permanent muscle memory.',
      curriculumTitle: 'The 30-Day Journey',
      phase1: 'The Foundation',
      phase1Desc: 'Open strings and the first 3 frets.',
      phase2: 'Moving Up',
      phase2Desc: 'Navigating the middle of the neck.',
      phase3: 'Upper Limits',
      phase3Desc: 'Mastering the dusty end above the 12th fret.',
      readyTitle: 'Ready to level up?',
      readyDesc: 'Join thousands of bassists mastering their instrument with modern technology.'
    },
    welcome: {
      title: 'Welcome to BassArena!',
      subtitle: 'Prepare your Gear',
      description: 'To practice your fretboard mastery effectively, we highly recommend using an audio interface (sound card) to plug your bass directly into your computer.',
      tip: 'Connection Tip',
      action: 'Once connected, go to the Settings tab to enable your microphone and select the correct input device.',
      cta: 'Got it, let\'s play!'
    },
    settings: {
      title: 'Settings',
      audioInput: 'Audio Input',
      noiseGate: 'Noise Gate',
      noiseGateHint: 'Increase this if notes appear when not playing.',
      pitchTolerance: 'Pitch Tolerance (cents)',
      stability: 'Stability (ms)',
      instrument: 'Instrument & Interface',
      darkMode: 'Dark Mode',
      fiveString: '5-String Bass Mode',
      multipleAttempts: 'Allow Multiple Attempts',
      unlockAllExercises: 'Unlock All Exercises',
      unlockAllExercisesHint: 'Access any task regardless of progress.',
      noteNaming: 'Note Naming Convention',
      timeLimit: 'Time Limit (sec)',
      unlockThreshold: 'Unlock Threshold',
      strictOctave: 'Strict Octave Validation',
      validateString: 'String Validation',
      beginnerMode: 'Beginner Mode (fretboard diagram)',
      language: 'App Language',
      primaryColor: 'Primary Theme Color',
      localization: 'Localization',
      trainingRules: 'Training Rules',
      liveMonitor: 'Live Monitor',
      startTest: 'Start Test',
      stopTest: 'Stop Test',
      testHint: 'Start test to verify audio input selection.',
      noteLabel: 'Note',
      dataManagement: 'Data Management',
      exportData: 'Export Progress (JSON)',
      importData: 'Import Progress (JSON)',
      importSuccess: 'Progress imported successfully!',
      importError: 'Failed to import. Invalid file format.',
      resetData: 'Reset All Data',
      resetSuccess: 'All data has been reset.',
      resetConfirmTitle: 'Are you absolutely sure?',
      resetConfirmDesc: 'This will permanently delete your settings, history, and fretboard mastery. This action cannot be undone.',
      colors: { blue: 'Blue', cyan: 'Cyan', green: 'Green', purple: 'Purple', red: 'Red', grey: 'Dark Grey' }
    },
    program: {
      title: 'Guided Mastery',
      mastered: 'Mastered',
      needAccuracy: 'accuracy to advance',
      day: 'Task',
      best: 'Best',
      noAttempts: 'No attempts yet',
      noNotesError: "No notes found for this exercise's criteria. Check your 4/5 string settings.",
      fret: 'Fret',
      scale: 'Scale',
      notes: 'notes',
      beginnerBadge: 'Beginner',
      srsMode: 'Spaced Repetition System',
      srsStatusActive: 'SRS ACTIVE',
      srsDueToday: 'Reviews Due Today',
      srsUpNext: 'Up Next',
      srsBadge: 'SRS Due',
      srsLevel: 'Retention Level',
      srsNext: 'Next review:',
      srsToday: 'Today!',
      srsMastered: 'Mastered (Level 5)',
      srsExplainer: 'How SRS Works',
      srsIntervals: 'Intervals: 1d, 3d, 7d, 14d, 30d',
      srsQueueTitle: 'Review Schedule',
      srsItemsPending: 'items pending',
      srsPermanent: 'Permanent',
      difficulty: 'Difficulty'
    },
    earTraining: {
      playMelody: "Play Melody", 
      yourTurn: "Your Turn!", 
      listening: "Listening...",
      feedbackAlmost: "Almost there!",
      feedbackRhythm: "Correct notes, but rhythm is off",
      feedbackPitch: "Good rhythm, wrong pitch sequence",
      feedbackEnding: "Listen again and focus on the ending",
      feedbackPerfect: "Perfect Match!",
      listenPrompt: "Listen to the melody, then play it back.",
      feedbackStart: "Good start!",
      feedbackNoteCaught: "Note caught!",
      feedbackWrong: "Wrong note! Start from the beginning.",
      restarting: "RESTARTING...",
      progressLabel: "PROGRESS",
      notesLabel: "NOTES",
      capturingLabel: "CAPTURING",
      tryAgain: "TRY AGAIN",
      playNextNote: "PLAY THE NEXT NOTE",
      listenAgain: "Listen Again",
      speed: "Playback Speed",
      tipFooter: "Tip: In this mode, the melody is randomized to a new root each time you start the session. Focus on relative intervals!"
    },
    rhythm: {
      title: 'Rhythm Academy',
      countIn: 'Prepare to play...',
      playAlong: 'Play the pattern now!',
      tempo: 'Tempo (BPM)',
      maxTempoReached: 'Max stable tempo reached',
      listen: 'Listen & Watch',
      play: 'Play Along',
      stop: 'Stop Session',
      tooSoon: 'Too soon',
      tooLate: 'Too late',
      missed: 'Missed',
      wrongNote: 'Wrong Pitch',
      feedbackTitle: 'Timing Analysis',
      perfect: 'Great timing!',
      summary: 'You hit {correct} out of {total} notes correctly.'
    },
    srs: {
      title: 'Mastering Memory with Spaced Repetition',
      intro: 'Fretboard memorization is a "forgetting" battle. SRS optimizes your practice by asking you to recall notes just as you are about to forget them.',
      curveTitle: 'The Forgetting Curve',
      curveDesc: 'Every time you recall a note correctly, the time it takes to forget it doubles. Our system tracks this automatically.',
      levelsTitle: 'Progression Levels',
      l1: 'Level 1: New knowledge (1 day interval)',
      l2: 'Level 2: Strengthening (3 days interval)',
      l3: 'Level 3: Intermediate (7 days interval)',
      l4: 'Level 4: Stable (14 days interval)',
      l5: 'Level 5: Long-term (30 days interval)',
      gold: 'Gold Level: Once you pass the 30-day review, that knowledge is considered permanent.',
      action: 'Enable SRS in the Program tab to start your optimized schedule.'
    },
    training: {
      title: 'Training Room',
      description: 'Choose your practice style. Target specific areas or fix persistent weak spots.',
      fretRange: 'Fret Range',
      strings: 'Strings',
      numQuestions: 'Questions',
      start: 'Start Session',
      fixWeakSpots: 'Fix My Weak Spots',
      weakSpotsDesc: 'Auto-generated drill targeting the notes you miss most or recognize slowest.',
      heatmap: 'Neck Mastery Map',
      heatmapDesc: 'Color-coded view of your performance across the entire fretboard.',
      topWeakNotes: 'Notes to Work On',
      neckCoverage: 'Neck Coverage',
      mostImproved: 'Most Improved',
      stats: 'Stats',
      attempts: 'Attempts',
      avgSpeed: 'Avg. Speed',
      accuracy: 'Accuracy',
      notPracticed: 'Not practiced yet',
      analyzeAndStart: 'Analyze & Start Drill',
      customSessionSetup: 'Custom Session Setup',
      maintenanceDrill: 'Maintenance Drill',
      masteredLabel: 'Mastered',
      weakSpotLabel: 'Weak Spot',
      deployCustom: 'Start Custom Drill'
    },
    session: {
      note: 'Note',
      score: 'Score',
      pluckString: 'String to pluck',
      hint: 'Hint',
      detected: 'DÉTECTÉ',
      stability: 'Stabilité',
      mute: 'Mute strings...',
      inputLevel: 'Input Level',
      active: 'Active',
      belowGate: 'Below Gate',
      perfect: 'Perfect!',
      silence: 'Silence',
      timeLeft: 'Time Left',
      cents: 'cents',
      sharp: 'Sharp',
      flat: 'Flat',
      loadingNotation: 'Loading Notation...',
      scaleDrill: 'Scale Drill',
      audioError: 'Audio Initialization Error',
      getReady: 'Get ready!'
    },
    summary: {
      title: 'Session Complete!',
      totalScore: 'Total Score',
      accuracy: 'Accuracy',
      avgSpeed: 'Avg. Speed',
      next: 'Next',
      replay: 'Replay Session',
      return: 'Return to Menu',
      failures: 'Notes to Review',
      etTime: 'Total Time',
      etMistakes: 'Mistakes'
    },
    theory: {
      title: 'Master the Theory',
      modules: 'Modules',
      navBasics: 'The Basics',
      navReading: 'Notation & Tabs',
      navGeometry: 'Fretboard Geometry',
      navConstruction: 'Full Scales',
      navPentatonic: 'Pentatonic Construction',
      navLibrary: 'Scale Library',
      navMemorizing: 'Memorization',
      navDetection: 'Detection Tips',
      basicsDesc: 'Before playing, you need to understand how your instrument is tuned. Standard tuning is based on 4ths.',
      stringsTitle: 'String Names',
      halfStepTitle: 'Demi-tons',
      halfStepDesc: 'Most notes have a sharp/flat between them, except for these two natural pairs:',
      readingDesc: 'We use two ways to read music on bass: the Staff (traditional) and the Tablature (geometric).',
      tabTitle: 'Tablature (TAB)',
      tabDesc: 'The 4 lines represent your strings. G is the top line, E is the bottom. The numbers tell you which fret to press.',
      staffTitle: 'Pentagrama',
      staffDesc: 'Bass music uses the Bass Clef. Each space and line represents a specific pitch in the western musical system.',
      exCMajor: 'Example: E string Scale (E to E)',
      exCMajorDesc: 'Notice how the notes move up the staff as the pitch gets higher.',
      geometryDesc: 'The bass neck is a repetitive grid. Learning the patterns makes finding notes intuitive.',
      ruleOf5: 'The Rule of 5',
      ruleOf5Desc: 'Standard tuning is "perfect fourths". This means the 5th fret on any string is the same note as the open string above it.',
      constructionTitle: 'Building Full Scales',
      constructionDesc: 'Scales are defined by "Steps": a Half-Step (H) is 1 fret, and a Whole-Step (W) is 2 frets.',
      majorPattern: 'Major Scale (Ionian): W - W - H - W - W - W - H',
      minorPattern: 'Natural Minor (Aeolian): W - H - W - W - H - W - W',
      exCMajorFull: 'Example: C Major Scale',
      exCMinorFull: 'Example: C Minor Scale',
      pentatonicTitle: 'The 5-Note "Penta" Logic',
      pentatonicDesc: 'Pentatonic scales are simplified versions of full scales. By removing "tension" notes, we get a group of 5 notes that almost always sound good together.',
      majorPentFormula: 'Major Pentatonic: 1 - 2 - 3 - 5 - 6',
      majorPentLogic: 'Start with a Major Scale and remove the 4th and 7th notes. These are the "half-step" neighbors that often create clashing tension.',
      minorPentFormula: 'Minor Pentatonic: 1 - b3 - 4 - 5 - b7',
      minorPentLogic: 'Start with a Natural Minor scale and remove the 2nd and 6th notes. This leaves a powerful, bluesy foundation.',
      guessingTitle: 'Identifying Scales',
      guessingDesc: 'The interval between the Root (1) and the 3rd note is your primary clue.',
      guessingMajor: 'Major: Root + 4 frets = Bright/Happy sound.',
      guessingMinor: 'Minor: Root + 3 frets = Dark/Sad sound.',
      libraryDesc: 'The Pentatonic is the most used scale in bass music. Practice visualizing these root-anchored shapes.',
      proTipTitle: 'Daily Habit',
      proTipDesc: '10 minutes of daily practice is 10x more effective than a 2-hour session once a week. Consistency builds muscle memory.',
      relativeHack: 'The "Relative" Hack',
      relativeHackDesc: 'Every Major Pentatonic scale shares the exact same notes as its Relative Minor. Find the root of the Major scale, then go down 3 frets to find its relative minor root.',
      visualBoxShapes: 'Visual Box Shapes',
      visualBoxShapesDesc: 'Focus on the shape rather than the names. Major and Minor pentatonics have consistent geometric "boxes" on the neck that you can slide to any key.',
      scaleExplorer: 'Explorer de Gammes',
      scaleExplorerDesc: 'Select a key and a scale type to visualize the patterns on the staff and tablature.',
      rootNote: 'Root Note',
      scaleType: 'Scale Type',
      majorPent: 'Major Pentatonic',
      minorPent: 'Minor Pentatonic',
      key: 'Key',
      detectionTitle: 'Best Practices for Audio Detection',
      detectionDesc: 'To ensure the engine tracks your bass accurately, keep these technical tips in mind:',
      tipTuner: 'Use the built-in Tuner often. Low frequencies can drift, and the engine needs you perfectly in pitch.',
      tipVolume: 'Maintain a high volume output. Whether using a mic or an interface, a weak signal is much harder to analyze.',
      tipMuting: 'Mute your notes before playing new ones. Overlapping vibrations can confuse the pitch tracker.',
      tipNoise: 'Limit input noise. Turn off fans and avoid practicing in loud rooms to keep the signal clear.'
    },
    metronome: {
      title: 'Metronome',
      bpm: 'BPM',
      volume: 'Volume',
      sound: 'Sound',
      beep: 'Beep',
      tick: 'Tick',
      wood: 'Woodblock',
      timeSignature: 'Time Signature',
      enabled: 'Metronome ON',
      disabled: 'Metronome OFF',
    },
    about: {
      title: 'About BassArena',
      subtitle: 'A project by Axel A.',
      description: 'BassArena was born out of a desire to create a practical, real-time tool for bassists of all levels. I wanted to design a method that makes learning fun and enjoyable, bridging the gap between traditional practice and modern interactive software.',
      contact: 'Contact',
      emailLabel: 'For requests, issues, or just to say hi:',
      footer: '© 2026 Axel A. - Made for bassists, by a bassist.',
      bassist: 'Bassist',
      coder: 'Coder',
      aiExperimentTitle: 'Engineering Note: AI Experimentation',
      aiExperimentBody: 'This application is an experimentation of quick application building with the help of AI. As an engineer, I believe we need to live with our time and get familiar with modern tooling to push the boundaries of what is possible.',
      aiExperimentJoke: '(Disclaimer: The photo with MonoNeon above is 100% real and NOT AI generated, I promise!)'
    },
    mic: {
      required: 'Microphone Access Required',
      description: 'We need access to your microphone to listen to your bass notes.',
      enable: 'Enable Microphone',
      denied: 'Microphone permission denied. Please enable it in your browser settings.',
      notFound: 'No microphone found. Please connect an audio interface or mic.',
      failed: 'Microphone permission was dismissed or failed. Please try again.',
      noMics: 'No microphones found',
      refreshHint: '* Refresh page if labels are missing.'
    }
  },
  fr: {
    nav: { home: 'Accueil', program: 'Programme', training: 'Entraînement', theory: 'Théorie', settings: 'Paramètres', setup: 'Config', tuner: 'Accordeur', about: 'À Propos' },
    landing: {
      heroTitle: 'Maîtrisez le manche de la basse en 30 jours',
      heroSubtitle: 'Détection de hauteur professionnelle en temps réel. Branchez votre basse, jouez les notes et obtenez un retour instantané.',
      ctaPrimary: 'Commencer l\'entraînement',
      ctaSecondary: 'Comment ça marche',
      feature1Title: 'Suivi en temps réel',
      feature1Desc: 'Notre moteur haute performance suit votre fréquence avec une précision millimétrée.',
      feature2Title: 'Programme de 30 jours',
      feature2Desc: 'Un programme granulaire étape par étape, des cordes à vide aux positions avancées.',
      feature3Title: 'Théorie intégrée',
      feature3Desc: 'Apprenez le "Pourquoi" derrière le "Comment" avec des modules de théorie et explorateurs de gammes.',
      feature4Title: 'Mémoire Optimisée',
      feature4Desc: 'Retenez ce que vous apprenez pour toujours grâce à notre Système de Répétition Espacée (SRS).',
      step1Title: 'Connectez',
      step1Desc: 'Branchez votre basse sur une interface audio ou utilisez votre micro.',
      step2Title: 'Jouez',
      step2Desc: 'Suivez les exercices quotidiens. L\'application écoute et valide chaque note.',
      step3Title: 'Maîtrisez',
      step3Desc: 'Suivez votre précision et votre vitesse tout en développant une mémoire musculaire permanente.',
      srsSectionTitle: 'Conçu pour la Rétention',
      srsSectionSubtitle: 'La Science de la Répétition Espacée',
      srsSectionDesc: 'La maîtrise du manche est une bataille contre l\'oubli. BassArena utilise les sciences cognitives pour planifier vos révisions au moment exact où votre mémoire commence à flancher, garantissant un passage vers la mémoire à long terme.',
      curriculumTitle: 'Le voyage de 30 jours',
      phase1: 'La Fondation',
      phase1Desc: 'Cordes à vide et les 3 premières frettes.',
      phase2: 'Prendre de la hauteur',
      phase2Desc: 'Naviguer au milieu du manche.',
      phase3: 'Limites supérieures',
      phase3Desc: 'Maîtriser la zone poussiéreuse au-delà de la 12ème frette.',
      readyTitle: 'Prêt à progresser ?',
      readyDesc: 'Rejoignez des milliers de bassistes qui maîtrisent leur instrument grâce à la technologie moderne.'
    },
    welcome: {
      title: 'Bienvenue sur BassArena !',
      subtitle: 'Bienvenue sur BassArena !',
      description: 'Pour pratiquer efficacement, nous recommandons vivement d\'utiliser une interface audio (carte son) pour brancher votre basse directement à votre ordinateur.',
      tip: 'Conseil de Connexion',
      action: 'Une fois connecté, rendez-vous dans l\'onglet Paramètres pour activer votre micro et sélectionner la bonne entrée.',
      cta: 'C\'est parti, on joue !'
    },
    settings: {
      title: 'Paramètres',
      audioInput: 'Entrée Audio',
      noiseGate: 'Seuil de Bruit',
      noiseGateHint: 'Augmentez ceci si des notes apparaissent sans jouer.',
      pitchTolerance: 'Tolérance de hauteur (cents)',
      stability: 'Stabilité (ms)',
      instrument: 'Instrument & Interface',
      darkMode: 'Mode Sombre',
      fiveString: 'Mode Basse 5 Cordes',
      multipleAttempts: 'Autoriser plusieurs essais',
      unlockAllExercises: 'Tout Débloquer',
      unlockAllExercisesHint: 'Accédez à n\'importe quelle tâche sans prérequis.',
      noteNaming: 'Convention de nom des notes',
      timeLimit: 'Limite de temps (sec)',
      unlockThreshold: 'Seuil de déblocage',
      strictOctave: 'Validation d\'octave stricte',
      validateString: 'Validation de la corde',
      beginnerMode: 'Mode Débutant (diagramme du manche)',
      language: 'Langue de l\'application',
      primaryColor: 'Couleur de thème principale',
      localization: 'Localization',
      trainingRules: 'Règles d\'Entraînement',
      liveMonitor: 'Moniteur Direct',
      startTest: 'Lancer le test',
      stopTest: 'Arrêter le test',
      testHint: 'Lancez le test pour vérifier l\'entrée audio.',
      noteLabel: 'Note',
      dataManagement: 'Gestion des Données',
      exportData: 'Exporter ma Progression (JSON)',
      importData: 'Importer ma Progression (JSON)',
      importSuccess: 'Progression importée avec succès !',
      importError: 'Échec de l\'importation. Format de fichier invalide.',
      resetData: 'Réinitialiser toutes les Données',
      resetSuccess: 'Toutes les données ont été réinitialisées.',
      resetConfirmTitle: 'Êtes-vous absolument sûr ?',
      resetConfirmDesc: 'Ceci supprimera définitivement vos paramètres, votre historique et votre maîtrise du manche. Cette action est irréversible.',
      colors: { blue: 'Bleu', cyan: 'Cyan', green: 'Vert', purple: 'Violet', red: 'Rouge', grey: 'Gris Foncé' }
    },
    program: {
      title: 'Maîtrise Guidée',
      mastered: 'Masterisé',
      needAccuracy: 'de précision pour avancer',
      day: 'Tâche',
      best: 'Meilleur',
      noAttempts: 'Aucune tentative',
      noNotesError: "Aucune note trouvée pour ces critères. Vérifiez vos réglages 4/5 cordes.",
      fret: 'Frette',
      scale: 'Gamme',
      notes: 'notes',
      beginnerBadge: 'Débutant',
      srsMode: 'Système de Répétition Espacée',
      srsStatusActive: 'SRS ACTIVÉ',
      srsDueToday: 'Révisions Dues',
      srsUpNext: 'À Venir',
      srsBadge: 'Répétition Due',
      srsLevel: 'Niveau de Rétention',
      srsNext: 'Prochain test :',
      srsToday: 'Aujourd\'hui !',
      srsMastered: 'Maîtrisé (Niveau 5)',
      srsExplainer: 'Comment fonctionne le SRS',
      srsIntervals: 'Intervalles : 1j, 3j, 7j, 14j, 30j',
      srsQueueTitle: 'Calendrier de Révision',
      srsItemsPending: 'éléments en attente',
      srsPermanent: 'Permanent',
      difficulty: 'Difficulté'
    },
    earTraining: {
      playMelody: "Jouer la Mélodie", 
      yourTurn: "À vous de jouer !", 
      listening: "Écoute...",
      feedbackAlmost: "Presque là !",
      feedbackRhythm: "Notes correctes, mais le rythme n'est pas bon",
      feedbackPitch: "Bon rythme, mauvaise séquence de notes",
      feedbackEnding: "Écoutez encore et concentrez-vous sur la fin",
      feedbackPerfect: "Match Parfait !",
      listenPrompt: "Écoutez la mélodie, puis jouez-la.",
      feedbackStart: "Bon départ !",
      feedbackNoteCaught: "Note validée !",
      feedbackWrong: "Mauvaise note ! Recommencez depuis le début.",
      restarting: "RÉINITIALISATION...",
      progressLabel: "PROGRESSION",
      notesLabel: "NOTES",
      capturingLabel: "CAPTURE",
      tryAgain: "RÉESSAYEZ",
      playNextNote: "JOUEZ LA NOTE SUIVANTE",
      listenAgain: "Réécouter",
      speed: "Vitesse de lecture",
      tipFooter: "Astuce : Dans ce mode, la mélodie est transposée à une tonique aléatoire à chaque session. Concentrez-vous sur les intervalles relatifs !"
    },
    rhythm: {
      title: 'Académie du Rythme',
      countIn: 'Préparez-vous...',
      playAlong: 'Jouez le motif !',
      tempo: 'Tempo (BPM)',
      maxTempoReached: 'Tempo max atteint',
      listen: 'Écouter & Regarder',
      play: 'Jouer',
      stop: 'Arrêter',
      tooSoon: 'Trop tôt',
      tooLate: 'Trop tard',
      missed: 'Manqué',
      wrongNote: 'Mauvaise note',
      feedbackTitle: 'Analyse du Timing',
      perfect: 'Timing parfait !',
      summary: 'Vous avez joué {correct} notes sur {total} correctement.'
    },
    srs: {
      title: 'Maîtriser la mémoire avec le SRS',
      intro: 'La mémorisation du manche est une bataille contre l\'oubli. Le SRS optimise votre pratique en vous demandant de vous demander de vous rappeler des notes juste au moment où vous allez les oublier.',
      curveTitle: 'La Courbe de l\'Oubli',
      curveDesc: 'Chaque fois que vous rappelez une note correctement, le temps nécessaire pour l\'oublier double. Notre système suit cela automatiquement.',
      levelsTitle: 'Niveaux de Progression',
      l1: 'Niveau 1 : Nouveau savoir (intervalle 1 jour)',
      l2: 'Niveau 2 : Renforcement (3 jours)',
      l3: 'Niveau 3 : Intermédiaire (7 jours)',
      l4: 'Niveau 4 : Stable (14 jours)',
      l5: 'Niveau 5 : Long terme (30 jours)',
      gold: 'Niveau Or : Une fois le test de 30 jours réussi, le savoir est considéré comme permanent.',
      action: 'Activez le SRS dans l\'onglet Programme pour démarrer votre planning optimisé.'
    },
    training: {
      title: 'Salle d\'Entraînement',
      description: 'Ciblez des zones spécifiques ou corrigez vos points faibles persistants.',
      fretRange: 'Plage de frettes',
      strings: 'Cordes',
      numQuestions: 'Questions',
      start: 'Démarrer la session',
      fixWeakSpots: 'Exercice auto-généré ciblant les notes que vous manquez le plus ou reconnaissez le plus lentement.',
      heatmap: 'Carte du Manche',
      heatmapDesc: 'Vue colorée de votre performance sur tout le manche.',
      topWeakNotes: 'Notes à travailler',
      neckCoverage: 'Couverture du Manche',
      mostImproved: 'Plus amélioré',
      stats: 'Stats',
      attempts: 'Essais',
      avgSpeed: 'Vitesse Moy.',
      accuracy: 'Précision',
      notPracticed: 'Jamais pratiqué',
      analyzeAndStart: 'Analyser & Démarrer',
      customSessionSetup: 'Configuration Personnalisée',
      maintenanceDrill: 'Exercice de Maintenance',
      masteredLabel: 'Maîtrisé',
      weakSpotLabel: 'Point Faible',
      deployCustom: 'Lancer l\'exercice'
    },
    session: {
      note: 'Note',
      score: 'Score',
      pluckString: 'Corde à pincer',
      hint: 'Indice',
      detected: 'DÉTECTÉ',
      stability: 'Stabilité',
      mute: 'Étouffez les cordes...',
      inputLevel: 'Niveau d\'entrée',
      active: 'Actif',
      belowGate: 'Below Gate',
      perfect: 'Parfait !',
      silence: 'Silence',
      timeLeft: 'Temps Restant',
      cents: 'cents',
      sharp: 'Trop haut',
      flat: 'Trop bas',
      loadingNotation: 'Chargement de la partition...',
      scaleDrill: 'Exercice de Gamme',
      audioError: 'Erreur d\'initialisation audio',
      getReady: 'Préparez-vous !'
    },
    summary: {
      title: 'Session Terminée !',
      totalScore: 'Score Total',
      accuracy: 'Précision',
      avgSpeed: 'Velocité Moy.',
      next: 'Suivant',
      replay: 'Rejouer la session',
      return: 'Retour au menu',
      failures: 'Notes à réviser',
      etTime: 'Temps Total',
      etMistakes: 'Erreurs'
    },
    theory: {
      title: 'Maîtrise de la Théorie',
      modules: 'Modules',
      navBasics: 'Les Bases',
      navReading: 'Notation & Tabs',
      navGeometry: 'Géométrie du Manche',
      navConstruction: 'Gammes Complètes',
      navPentatonic: 'Construction Pentatonique',
      navLibrary: 'Bibliothèque',
      navMemorizing: 'Mémorisation',
      navDetection: 'Conseils de Détection',
      basicsDesc: 'Avant de jouer, il faut comprendre l\'accordage. L\'accordage standard est basé sur les quartes.',
      stringsTitle: 'Nom des cordes',
      halfStepTitle: 'Demi-tons',
      halfStepDesc: 'La plupart des notes ont un dièse/bémol entre elles, sauf ces deux paires naturelles :',
      readingDesc: 'On utilise la Portée (traditionnelle) et la Tablature (géométrique).',
      tabTitle: 'Tablature (TAB)',
      tabDesc: 'Les 4 lignes représentent vos cordes. Sol est en haut, Mi en bas.',
      staffTitle: 'Portée',
      staffDesc: 'La basse utilise la Clé de Fa.',
      exCMajor: 'Exemple : Gamme sur la corde de Mi',
      exCMajorDesc: 'Notez comment les notes montent sur la portée.',
      geometryDesc: 'Le manche de la basse est une grille répétitive.',
      ruleOf5: 'La Règle de 5',
      ruleOf5Desc: 'La 5ème frette est la même note que la corde à vide supérieure.',
      constructionTitle: 'Construction des Gammes',
      constructionDesc: 'On utilise des "Tons" (T = 2 frettes) et des "Demi-tons" (D = 1 frette).',
      majorPattern: 'Gamme Majeure (Ionien) : T - T - D - T - T - T - D',
      minorPattern: 'Gamme Mineure (Eolien) : T - D - T - T - D - T - T',
      exCMajorFull: 'Example: Gamme de Do Majeur',
      exCMinorFull: 'Example: Gamme de Do Mineur',
      pentatonicTitle: 'La Logique des 5 Notes',
      pentatonicDesc: 'Les gammes pentatoniques sont des versions simplifiées des gammes complètes. En retirant les notes de "tension", on obtient un groupe de 5 notes que sonnent presque toujours bien ensemble.',
      majorPentFormula: 'Pentatronique Majeure : 1 - 2 - 3 - 5 - 6',
      majorPentLogic: 'Partez d\'une gamme Majeure et retirez la 4te et la 7me note. Ce sont les voisins en "demi-ton" qui créent souvent des dissonances.',
      minorPentFormula: 'Pentatonique Mineure : 1 - b3 - 4 - 5 - b7',
      minorPentLogic: 'Partez d\'une gamme Mineure naturelle et retirez la 2de et la 6te note. Cela laisse une base puissante et bluesy.',
      guessingTitle: 'Identifier les Gammes',
      guessingDesc: 'L\'intervalle entre la Tonique (1) et la 3ème note est votre indice principal.',
      guessingMajor: 'Majeur : Fondamentale + 4 frettes = Son Joyeux/Clair.',
      guessingMinor: 'Mineur : Fondamentale + 3 frettes = Son Sombre/Triste.',
      libraryDesc: 'La Pentat संतुलित is the most used scale in bass music. Practice visualizing these root-anchored shapes.',
      proTipTitle: 'Habitude Quotidienne',
      proTipDesc: '10 minutes par jour valent mieux qu\'une séance de 2 heures par semaine.',
      relativeHack: 'L\'Astuce Relative',
      relativeHackDesc: 'Chaque gamme Majeure partage les mêmes notes que sa Mineure Relative. Trouvez la tonique Majeure, descendez de 3 frettes pour trouver la relative mineure.',
      visualBoxShapes: 'Formes Géométriques',
      visualBoxShapesDesc: 'Concentrez-vous sur la forme plutôt que sur les noms. Les pentatoniques ont des "boîtes" géométriques que vous pouvez déplacer.',
      scaleExplorer: 'Explorateur de Gammes',
      scaleExplorerDesc: 'Choisissez une tonalité et un type de gamme pour visualiser les schémas sur la portée et la tablature.',
      rootNote: 'Tonique',
      scaleType: 'Type de Gamme',
      majorPent: 'Pentatonique Majeure',
      minorPent: 'Pentatonique Mineure',
      key: 'Tonalité',
      detectionTitle: 'Optimiser la Détection Audio',
      detectionDesc: 'Pour que le moteur de détection suive parfaitement votre basse, gardez ces conseils en tête :',
      tipTuner: 'Utilisez l\'Accordeur souvent. Les basses fréquences bougent, et le moteur a besoin d\'une justesse parfaite.',
      tipVolume: 'Assurez un volume de sortie élevé. Un signal faible est beaucoup plus difficile à analyser avec précision.',
      tipMuting: 'Étouffez vos notes avant d\'en jouer de nouvelles. Les vibrations résiduelles peuvent perturber le tracker.',
      tipNoise: 'Limitez les bruits parasites. Évitez les ventilateurs ou les pièces bruyantes pour garder un signal propre.'
    },
    metronome: {
      title: 'Métronome',
      bpm: 'BPM',
      volume: 'Volume',
      sound: 'Son',
      beep: 'Bip',
      tick: 'Tic',
      wood: 'Woodblock',
      timeSignature: 'Signature rythmique',
      enabled: 'Métronome ON',
      disabled: 'Métronome OFF',
    },
    about: {
      title: 'À Propos de BassArena',
      subtitle: 'Un projet personnel d\'Axel A.',
      description: 'BassArena est né d\'une volonté de créer un outil pratique et temps réel pour les bassistes de tous niveaux. Je voulais rendre l\'apprentissage plus ludique et plaisant, en faisant le pont entre la pratique traditionnelle et les technologies interactive modernes.',
      contact: 'Contact',
      emailLabel: 'Pour toute demande, problème ou simplement dire bonjour :',
      footer: '© 2026 Axel A. - Fait par un bassiste, pour les bassistes.',
      bassist: 'Bassiste',
      coder: 'Codeur',
      aiExperimentTitle: 'Note Technique : Expérimentation IA',
      aiExperimentBody: 'Cette application est une expérimentation sur la construction rapide d\'applications avec l\'aide de l\'IA. En tant qu\'ingénieur, je pense qu\'il faut vivre avec son temps et se familiariser avec les outils modernes pour repousser les limites du possible.',
      aiExperimentJoke: '(Note : La photo avec MonoNeon ci-dessus est bien réelle et n\'a PAS été générée par IA, promis !)'
    },
    mic: {
      required: 'Accès au Micro Requis',
      description: 'Nous avons besoin d\'accéder à votre microphone pour écouter vos notes de basse.',
      enable: 'Activer le Microphone',
      denied: 'Accès au micro refusé. Veuillez l\'activer dans les paramètres de votre navigateur.',
      notFound: 'Microphone introuvable. Veuillez connecter une interface audio ou un micro.',
      failed: 'La demande de permission pour le microphone a échoué. Veuillez réessayer.',
      noMics: 'Aucun microphone trouvé',
      refreshHint: '* Rafraîchir si les étiquettes manquent.'
    }
  },
  es: {
    nav: { home: 'Inicio', program: 'Programa', training: 'Entrenamiento', theory: 'Teoría', settings: 'Ajustes', setup: 'Configuración', tuner: 'Afinador', about: 'Acerca de' },
    landing: {
      heroTitle: 'Domina el diapasón del bajo en 30 días',
      heroSubtitle: 'Detección de tono profesional en tiempo real. Conecta tu bajo, toca las notas y recibe feedback instantáneo.',
      ctaPrimary: 'Empezar entrenamiento',
      ctaSecondary: 'Cómo funciona',
      feature1Title: 'Seguimiento en tiempo real',
      feature1Desc: 'Nuestro motor de alto rendimiento rastrea tu frecuencia con precisión milimétrica.',
      feature2Title: 'Currículo de 30 días',
      feature2Desc: 'Un programa paso a paso, desde cuerdas al aire hasta posiciones avanzadas.',
      feature3Title: 'Teoría integrada',
      feature3Desc: 'Aprende el "Por qué" detrás del "Qué" con módulos de teoría y exploradores de escalas.',
      feature4Title: 'Optimización de Memoria',
      feature4Desc: 'Retén lo que aprendes para siempre con nuestro Sistema de Repetición Espaciada (SRS).',
      step1Title: 'Conecta',
      step1Desc: 'Conecta tu bajo a una interfaz de audio or usa tu micrófono.',
      step2Title: 'Toca',
      step2Desc: 'Sigue los ejercicios diarios. La aplicación escucha y valida cada nota.',
      step3Title: 'Domina',
      step3Desc: 'Rastrea tu precisión y velocidad mientras construyes memoria muscular permanente.',
      srsSectionTitle: 'Diseñado para la Retención',
      srsSectionSubtitle: 'La Ciencia de la Repetición Espaciada',
      srsSectionDesc: 'Dominar el diapasón es una batalla contra el olvido. BassArena utiliza la ciencia cognitiva para programar revisiones en el momento exacto en que tu memoria comienza a desvanecerse.',
      curriculumTitle: 'El viaje de 30 días',
      phase1: 'La base',
      phase1Desc: 'Cuerdas al aire y los primeros 3 trastes.',
      phase2: 'Subiendo',
      phase2Desc: 'Navegando por el centro del mástil.',
      phase3: 'Límites superiores',
      phase3Desc: 'Dominando el final polvoriento por encima del traste 12.',
      readyTitle: '¿Listo para subir de nivel?',
      readyDesc: 'Únete a miles de bajistas que dominan su instrumento con tecnología moderna.'
    },
    welcome: {
      title: '¡Bienvenido a BassArena!',
      subtitle: 'Prepara tu equipo',
      description: 'Para practicar el dominio del mástil de forma eficaz, te recomendamos encarecidamente utilizar una interfaz de audio (tarjeta de sonido) para conectar el bajo directamente al ordenador.',
      tip: 'Consejo de conexión',
      action: 'Once conectado, ve a la pestaña Ajustes para activar el micrófono y seleccionar el dispositivo de entrada correcto.',
      cta: '¡Entendido, a tocar!'
    },
    settings: {
      title: 'Ajustes',
      audioInput: 'Entrada de audio',
      noiseGate: 'Puerta de ruido',
      noiseGateHint: 'Aumenta esto si aparecen notas sin estar tocando.',
      pitchTolerance: 'Tolerancia de afinación (cents)',
      stability: 'Estabilidad (ms)',
      instrument: 'Instrumento e interfaz',
      darkMode: 'Modo oscuro',
      fiveString: 'Bajo de 5 cuerdas',
      multipleAttempts: 'Permitir varios intentos',
      unlockAllExercises: 'Desbloquear todo',
      unlockAllExercisesHint: 'Permite practicar cualquier tarea sin importar el progreso.',
      noteNaming: 'Nomenclatura de notas',
      timeLimit: 'Tiempo límite (seg)',
      unlockThreshold: 'Umbral de desbloqueo',
      strictOctave: 'Validación de octava estricta',
      validateString: 'Validación de cuerda',
      beginnerMode: 'Modo principiante (diagrama del mástil)',
      language: 'Idioma de la aplicación',
      primaryColor: 'Color de tema principal',
      localization: 'Localización',
      trainingRules: 'Reglas de entrenamiento',
      liveMonitor: 'Monitor en vivo',
      startTest: 'Iniciar prueba',
      stopTest: 'Detener prueba',
      testHint: 'Inicia la prueba para verificar la entrada de audio.',
      noteLabel: 'Nota',
      dataManagement: 'Gestion de datos',
      exportData: 'Exportar progreso (JSON)',
      importData: 'Importar progreso (JSON)',
      importSuccess: '¡Progreso importado con éxito!',
      importError: 'Error al importar. Formato de archivo inválido.',
      resetData: 'Reiniciar todos los datos',
      resetSuccess: 'Todos los datos han sido reiniciados.',
      resetConfirmTitle: '¿Estás absolutamente seguro?',
      resetConfirmDesc: 'Esto eliminará permanentemente tus ajustes, historial y maestría del mástil. Esta acción no se puede deshacer.',
      colors: { blue: 'Azul', cyan: 'Cian', green: 'Verde', purple: 'Púrpura', red: 'Rojo', grey: 'Gris oscuro' }
    },
    program: {
      title: 'Maestría Guiada',
      mastered: 'Dominado',
      needAccuracy: 'de precisión necesaria para avanzar',
      day: 'Tarea',
      best: 'Mejor',
      noAttempts: 'Sin intentos aún',
      noNotesError: "No se encontraron notas para los criterios de este ejercicio. Revisa tus ajustes de 4/5 cuerdas.",
      fret: 'Traste',
      scale: 'Escala',
      notes: 'notes',
      beginnerBadge: 'Principiante',
      srsMode: 'Sistema de Repetición Espaciada',
      srsStatusActive: 'SRS ACTIVO',
      srsDueToday: 'Repeticiones Pendientes',
      srsUpNext: 'Próximamente',
      srsBadge: 'Repetición Pendiente',
      srsLevel: 'Nivel de Retención',
      srsNext: 'Próxima revisión:',
      srsToday: '¡Hoy!',
      srsMastered: 'Dominado (Nivel 5)',
      srsExplainer: 'Cómo funciona SRS',
      srsIntervals: 'Intervalos: 1d, 3d, 7d, 14d, 30d',
      srsQueueTitle: 'Agenda de Revisión',
      srsItemsPending: 'elementos pendientes',
      srsPermanent: 'Permanente',
      difficulty: 'Dificultad'
    },
    earTraining: {
      playMelody: "Reproducir Melodía", 
      yourTurn: "¡Tu turno!", 
      listening: "Escuchando...",
      feedbackAlmost: "¡Casi lo tienes!",
      feedbackRhythm: "Notas correctas, pero el ritmo no es bueno",
      feedbackPitch: "Buen ritmo, secuencia de notas incorrecta",
      feedbackEnding: "Escucha de nuevo y concéntrate en el final",
      feedbackPerfect: "¡Combinación Perfecta!",
      listenPrompt: "Escucha la melodía y luego repítela.",
      feedbackStart: "¡Buen comienzo!",
      feedbackNoteCaught: "¡Nota capturada!",
      feedbackWrong: "¡Nota incorrecta! Empieza desde el principio.",
      restarting: "REINICIANDO...",
      progressLabel: "PROGRESO",
      notesLabel: "NOTAS",
      capturingLabel: "CAPTURANDO",
      tryAgain: "INTÉNTALO DE NUEVO",
      playNextNote: "TOCA LA SIGUIENTE NOTA",
      listenAgain: "Escuchar de nuevo",
      speed: "Velocidad de reproducción",
      tipFooter: "Consejo: En este modo, la melodía se transporta a una tónica aleatoria en cada sesión. ¡Céntrate en los intervalos relativos!"
    },
    rhythm: {
      title: 'Academia del Ritmo',
      countIn: 'Prepárate...',
      playAlong: '¡Toca el patrón ahora!',
      tempo: 'Tempo (BPM)',
      maxTempoReached: 'Tempo máximo alcanzado',
      listen: 'Escuchar y ver',
      play: 'Tocar',
      stop: 'Detener',
      tooSoon: 'Demasiado pronto',
      tooLate: 'Demasiado tarde',
      missed: 'Fallado',
      wrongNote: 'Nota incorrecta',
      feedbackTitle: 'Análisis de tiempo',
      perfect: '¡Tiempo perfecto!',
      summary: 'Tocaste {correct} de {total} notas correctamente.'
    },
    srs: {
      title: 'Dominando la memoria con SRS',
      intro: 'Memorizar el mástil es una batalla contra el olvido. El SRS optimiza tu práctica pidiéndote que recuerdes las notas justo cuando estás a punto de olvidarlas.',
      curveTitle: 'La Courve de l\'Oubli',
      curveDesc: 'Cada vez que recuerdas una nota correctamente, el tiempo que tardas en olvidarla se duplica. Nuestro sistema lo rastrea automáticamente.',
      levelsTitle: 'Niveeles de Progresión',
      l1: 'Level 1: Nuevo conocimiento (1 día)',
      l2: 'Nivel 2: Refuerzo (3 días)',
      l3: 'Nivel 3: Intermedio (7 días)',
      l4: 'Nivel 4: Estable (14 días)',
      l5: 'Nivel 5: Largo plazo (30 días)',
      gold: 'Nivel Oro: Una vez que pasas la revisión de 30 días, ese conocimiento se considera permanente.',
      action: 'Activa el SRS en la pestaña Programa para iniciar tu horario optimizado.'
    },
    training: {
      title: 'Sala de Práctica',
      description: 'Elige tu estilo de práctica. Enfócate en áreas específicas o corrige tus puntos débiles persistentes.',
      fretRange: 'Rango de trastes',
      strings: 'Cuerdas',
      numQuestions: 'Preguntas',
      start: 'Empezar sesión',
      fixWeakSpots: 'Corregir puntos débiles',
      weakSpotsDesc: 'Ejercicio auto-generado que apunta a las notas que más fallas o reconoces más lento.',
      heatmap: 'Mapa del mástil',
      heatmapDesc: 'Vista coloreada de tu rendimiento en todo el diapasón.',
      topWeakNotes: 'Notas para trabajar',
      neckCoverage: 'Cobertura del mástil',
      mostImproved: 'Más mejorado',
      stats: 'Estadísticas',
      attempts: 'Intentos',
      avgSpeed: 'Vel. media',
      accuracy: 'Precisión',
      notPracticed: 'No practicado aún',
      analyzeAndStart: 'Analizar y empezar',
      customSessionSetup: 'Configuración personalizada',
      maintenanceDrill: 'Ejercicio de mantenimiento',
      masteredLabel: 'Dominado',
      weakSpotLabel: 'Punto débil',
      deployCustom: 'Iniciar entrenamiento personalizado'
    },
    session: {
      note: 'Note',
      score: 'Puntos',
      pluckString: 'Cuerda a tocar',
      hint: 'Pista',
      detected: 'DETECTADA',
      stability: 'Estabilidad',
      mute: 'Silencia las cuerdas...',
      inputLevel: 'Nivel de entrada',
      active: 'Activo',
      belowGate: 'Bajo el umbral',
      perfect: '¡Perfecto!',
      silence: 'Silencio',
      timeLeft: 'Tiempo restante',
      cents: 'cents',
      sharp: 'Agudo',
      flat: 'Grave',
      loadingNotation: 'Cargando partitura...',
      scaleDrill: 'Ejercicio de escala',
      audioError: 'Error de inicialización de audio',
      getReady: '¡Prepárate!'
    },
    summary: {
      title: '¡Sesión terminada!',
      totalScore: 'Puntuación total',
      accuracy: 'Precisión',
      avgSpeed: 'Velocidad media',
      next: 'Siguiente',
      replay: 'Repetir sesión',
      return: 'Volver al menú',
      failures: 'Notas para revisar',
      etTime: 'Tiempo Total',
      etMistakes: 'Errores'
    },
    theory: {
      title: 'Maestría en Teoría',
      modules: 'Módulos',
      navBasics: 'Lo básico',
      navReading: 'Notación y Tabs',
      navGeometry: 'Geometría del mástil',
      navConstruction: 'Escalas completas',
      navPentatonic: 'Construcción pentatónica',
      navLibrary: 'Biblioteca de escalas',
      navMemorizing: 'Memorización',
      navDetection: 'Consejos de detección',
      basicsDesc: 'Antes de tocar, debes entender cómo se afina tu instrumento. La afinación estándar se basa en cuartas.',
      stringsTitle: 'Nombre de las cuerdas',
      halfStepTitle: 'Semitonos',
      halfStepDesc: 'Casi todas las notas tienen un sostenido/bemol entre ellas, excepto estos dos pares naturales:',
      readingDesc: 'Usamos dos formas de leer música en el bajo: el Pentagrama (tradicional) y la Tablatura (geométrica).',
      tabTitle: 'Tablature (TAB)',
      tabDesc: 'The 4 líneas representan tus cuerdas. Sol (G) es la línea superior, Mi (E) es la inferior. Los números indican qué traste presionar.',
      staffTitle: 'Pentagrama',
      staffDesc: 'La música de bajo usa la Clave de Fa. Cada espacio y línea representa un tono específico en el sistema musical occidental.',
      exCMajor: 'Ejemplo: Escala en la cuerda Mi (de Mi a Mi)',
      exCMajorDesc: 'Observa cómo las notas suben en el pentagrama a medida que el tono se vuelve más agudo.',
      geometryDesc: 'El mástil del bajo es una cuadrícula repetitiva. Aprender los patrones hace que encontrar notas sea intuitivo.',
      ruleOf5: 'La regla del 5',
      ruleOf5Desc: 'La afinación estándar es de "cuartas perfectas". Esto significa que el 5º traste de cualquier cuerda es la misma nota que la cuerda al aire superior.',
      constructionTitle: 'Construcción de escalas completas',
      constructionDesc: 'Las escalas se definen por "Pasos": un Semitono (S) es 1 traste, y un Tono (T) son 2 trastes.',
      majorPattern: 'Escala Mayor (Jónica): T - T - S - T - T - T - S',
      minorPattern: 'Menor Natural (Eólica): T - S - T - T - S - T - T',
      exCMajorFull: 'Ejemplo: Escala de Do Mayor',
      exCMinorFull: 'Ejemplo: Escala de Do Menor',
      pentatonicTitle: 'La lógica de 5 notas "Penta"',
      pentatonicDesc: 'Las escalas pentatónicas son versiones simplificados de las escalas completas. Al eliminar las notas de "tensión", obtenemos un grupo de 5 notas que casi siempre suenan bien juntas.',
      majorPentFormula: 'Pentatónica Mayor: 1 - 2 - 3 - 5 - 6',
      majorPentLogic: 'Comienza con una Escala Mayor y elimina la 4ª y la 7ª nota. Estas son las vecinas de "semitono" que a menudo crean una tensión discordante.',
      minorPentFormula: 'Pentatónica Menor: 1 - b3 - 4 - 5 - b7',
      minorPentLogic: 'Comienza con una escala Menor natural y elimina la 2ª y la 6ª nota. Esto deja una base potente y blusera.',
      guessingTitle: 'Identificación de escalas',
      guessingDesc: 'The interval between the Root (1) and the 3rd note is your primary clue.',
      guessingMajor: 'Mayor: Tónica + 4 trastes = Sonido brillante/alegre.',
      guessingMinor: 'Menor: Tónica + 3 trastes = Sonido oscuro/triste.',
      libraryDesc: 'La pentatónica es la escala más utilizada en el bajo. Practica la visualización de estas formas ancladas en la raíz.',
      proTipTitle: 'Hábito diario',
      proTipDesc: '10 minutos de práctica diaria es 10 veces más efectivo que una sesión de 2 horas una vez a la semana. La consistencia crea memoria muscular.',
      relativeHack: 'El truco de las "relativas"',
      relativeHackDesc: 'Cada escala Mayor comparte exactamente las mismas notas que su Menor Relativa. Encuentra la raíz de la escala Mayor, luego baja 3 trastes para encontrar su raíz menor relativa.',
      visualBoxShapes: 'Formes geométricas',
      visualBoxShapesDesc: 'Concéntrate en la forma más que en los nombres. Las pentatónicas mayor y menor tienen "cajas" geométricas consistentes en el mástil que puedes deslizar a cualquier clave.',
      scaleExplorer: 'Explorer de Gammes',
      scaleExplorerDesc: 'Selecciona una clave y un tipo de escala para visualizar los patrones en el pentagrama y la tablatura.',
      rootNote: 'Nota raíz',
      scaleType: 'Tipo de escala',
      majorPent: 'Pentatónica Mayor',
      minorPent: 'Pentatónica Menor',
      key: 'Clave',
      detectionTitle: 'Consejos para la detección de audio',
      detectionDesc: 'Para que el motor de detección rastree tu bajo con precisión, sigue estos consejos técnicos:',
      tipTuner: 'Usa el afinador integrado con frecuencia. Las notas graves pueden fluctuar y el motor requiere una afinación perfecta.',
      tipVolume: 'Mantén un volumen de salida alto. Ya sea con micro o interfaz, una señal débil es mucho más difícil de procesar.',
      tipMuting: 'Silencia las cuerdas antes de tocar nuevas notas. Las vibraciones residuales pueden confundir al rastreador.',
      tipNoise: 'Limita el ruido de entrada. Apaga ventiladores y evita habitaciones ruidosas para mantener la señal limpia.'
    },
    metronome: {
      title: 'Metrónomo',
      bpm: 'BPM',
      volume: 'Volume',
      sound: 'Sonido',
      beep: 'Beep',
      tick: 'Tick',
      wood: 'Bloque de madera',
      timeSignature: 'Compás',
      enabled: 'Metrónomo ACTIVADO',
      disabled: 'Metronome DESACTIVADO',
    },
    about: {
      title: 'Acerca de BassArena',
      subtitle: 'A proyecto de Axel A.',
      description: 'BassArena nació del deseo de crear una herramienta práctica y en tiempo real para bajistas de todos los niveles. Mi objetivo era diseñar un método que hiciera el aprendizaje divertido y ameno, uniendo la práctica tradicional con el software interactivo moderno.',
      contact: 'Contacto',
      emailLabel: 'Para peticiones, problemas o simplemente saludar:',
      footer: '© 2026 Axel A. - Hecho para bajistas, por un bajista.',
      bassist: 'Bajista',
      coder: 'Programador',
      aiExperimentTitle: 'Nota de ingeniería: Experimentación con IA',
      aiExperimentBody: 'Esta application es un experimento sobre la construcción rápida de aplicaciones con la ayuda de la IA. Como ingeniero, creo que debemos vivir en nuestro tiempo y familiarizarnos con las herramientas modernas para ampliar los límites de lo posible.',
      aiExperimentJoke: '(Aviso: ¡La foto con MonoNeon de arriba es 100% real y NO fue generada por IA, lo prometo!)'
    },
    mic: {
      required: 'Acceso al micrófono requerido',
      description: 'Necesitamos acceso a tu micrófono para escuchar las notas de tu bajo.',
      enable: 'Activar micrófono',
      denied: 'Permiso de micrófono denegado. Por favor, actívalo en los ajustes de tu navegador.',
      notFound: 'No se encontró ningún micrófono. Por favor, conecta una interfaz de audio o micro.',
      failed: 'La solicitud de permiso para el micrófono falló. Por favor, inténtalo de nuevo.',
      noMics: 'No se encontraron micrófonos',
      refreshHint: '* Recarga la página si faltan los nombres de los dispositivos.'
    }
  }
};
